import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink, type PlaidLinkOnExit, type PlaidLinkOnSuccess } from 'react-plaid-link';
import toast from 'react-hot-toast';
import { plaidApi } from '../../services/plaid-api';
import { handleApiError } from '../../services/api';

interface PlaidReconnectProps {
  dataSourceId: string;
  institutionName: string;
  /** Called once the flow is over, whether it repaired the connection or not. */
  onDone: (reconnected: boolean) => void;
}

/**
 * Opens Plaid Link in update mode to repair a broken connection. Plaid keeps the
 * Item's access token, so this restores the existing source rather than creating
 * a second one — the history and its transactions stay attached.
 *
 * Renders nothing; Link is an overlay.
 */
export const PlaidReconnect: React.FC<PlaidReconnectProps> = ({
  dataSourceId,
  institutionName,
  onDone,
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    plaidApi
      .createUpdateLinkToken(dataSourceId)
      .then((response) => {
        if (!cancelled) setLinkToken(response.linkToken);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(handleApiError(err) || 'Could not start the reconnect');
        onDone(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dataSourceId, onDone]);

  const onSuccess = useCallback<PlaidLinkOnSuccess>(async () => {
    try {
      // No token exchange in update mode — the backend re-checks the Item and
      // clears the error state only if the bank actually accepted the login.
      await plaidApi.completeReconnect(dataSourceId);
      toast.success(`${institutionName} reconnected — syncing now`);
      onDone(true);
    } catch (err) {
      toast.error(handleApiError(err) || 'The connection still needs attention');
      onDone(false);
    }
  }, [dataSourceId, institutionName, onDone]);

  const onExit = useCallback<PlaidLinkOnExit>(
    (err) => {
      if (err) toast.error('Reconnect cancelled');
      onDone(false);
    },
    [onDone],
  );

  const { open, ready } = usePlaidLink({ token: linkToken, onSuccess, onExit });

  useEffect(() => {
    if (ready && linkToken) open();
  }, [ready, linkToken, open]);

  return null;
};

export default PlaidReconnect;
